import * as React from "react";

import { api } from "@repo/backend";
import { Button } from "@repo/ui/components/button";
import { useConfirmationDialog } from "@repo/ui/components/confirmation-dialog-provider";
import { DataTableColumnHeader } from "@repo/ui/components/data-table/data-table-column-header";
import { DataTableToolbar } from "@repo/ui/components/data-table/data-table-toolbar";
import { Field, FieldDescription, FieldLabel } from "@repo/ui/components/field";
import { Input } from "@repo/ui/components/input";
import { RelativeTimeCard } from "@repo/ui/components/relative-time-card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@repo/ui/components/sheet";
import { Textarea } from "@repo/ui/components/textarea";
import { useDataTable } from "@repo/ui/hooks/use-data-table";
import { createFileRoute } from "@tanstack/react-router";
import { type StockFeatures, ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { Loader2Icon, PencilIcon, PlusIcon, Trash2Icon } from "lucide-react";
import { toast } from "sonner";

import { DataTable } from "@/components/data-table";

export const Route = createFileRoute("/_authed/users/allow-list")({
  head: () => ({ meta: [{ title: "Email allow-list" }] }),
  component: RouteComponent,
});

type AllowListEntry = (typeof api.admin.listEmailAllowList)["_returnType"][number];

type AllowListFormValue = {
  email: string;
  description: string;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof ConvexError) return String(error.data);
  if (error instanceof Error) return error.message;
  return fallback;
};

function AddAllowListEntrySheet() {
  const addEntry = useMutation(api.admin.addEmailToAllowList);
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState<AllowListFormValue>({ email: "", description: "" });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const reset = () => setValue({ email: "", description: "" });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await addEntry({
        email: value.email.trim().toLowerCase(),
        description: value.description.trim() || undefined,
      });
      reset();
      setOpen(false);
      toast.success("Email added to the allow-list.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to add email."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) reset();
      }}
    >
      <SheetTrigger
        render={(props) => (
          <Button {...props} size="sm">
            <PlusIcon />
            Add email
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>Add allow-listed email</SheetTitle>
            <SheetDescription>
              Allow a person to sign in or create an account with this exact email address.
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <Field>
                <FieldLabel htmlFor="allow-list-email">Email</FieldLabel>
                <Input
                  id="allow-list-email"
                  type="email"
                  value={value.email}
                  onChange={(event) =>
                    setValue((current) => ({ ...current, email: event.target.value }))
                  }
                  placeholder="name@example.com"
                  required
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="allow-list-description">Description</FieldLabel>
                <Textarea
                  id="allow-list-description"
                  value={value.description}
                  onChange={(event) =>
                    setValue((current) => ({ ...current, description: event.target.value }))
                  }
                  placeholder="Why this email is allowed"
                  rows={4}
                />
                <FieldDescription>Optional note for admins.</FieldDescription>
              </Field>
            </div>

            <div className="border-t px-4 pt-4">
              <Button className="w-full" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                Add email
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EditAllowListEntrySheet({ entry }: { entry: AllowListEntry }) {
  const updateEntry = useMutation(api.admin.updateEmailAllowListEntry).withOptimisticUpdate(
    (localStore, args) => {
      const entries = localStore.getQuery(api.admin.listEmailAllowList, {});
      if (!entries) return;

      localStore.setQuery(
        api.admin.listEmailAllowList,
        {},
        entries.map((item) =>
          item._id === args.id
            ? { ...item, description: args.description?.trim() || undefined }
            : item,
        ),
      );
    },
  );
  const [open, setOpen] = React.useState(false);
  const [description, setDescription] = React.useState(entry.description ?? "");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await updateEntry({
        id: entry["_id"],
        description: description.trim() || undefined,
      });
      setOpen(false);
      toast.success("Allow-list note updated.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to update allow-list entry."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) setDescription(entry.description ?? "");
      }}
    >
      <SheetTrigger
        render={(props) => (
          <Button {...props} size="icon-lg" variant="ghost" aria-label={`Edit ${entry.email}`}>
            <PencilIcon />
          </Button>
        )}
      >
        Open
      </SheetTrigger>
      <SheetContent className="data-[side=right]:w-full data-[side=right]:sm:max-w-md">
        <form className="flex h-full flex-col" onSubmit={handleSubmit}>
          <SheetHeader>
            <SheetTitle>Edit allow-list note</SheetTitle>
            <SheetDescription>{entry.email}</SheetDescription>
          </SheetHeader>

          <div className="flex flex-1 flex-col justify-between">
            <div className="grid gap-5 px-4">
              <Field>
                <FieldLabel htmlFor={`allow-list-description-${String(entry["_id"])}`}>
                  Description
                </FieldLabel>
                <Textarea
                  id={`allow-list-description-${String(entry["_id"])}`}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Why this email is allowed"
                  rows={5}
                />
              </Field>
            </div>

            <div className="border-t px-4 pt-4">
              <Button className="w-full" type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2Icon className="size-4 animate-spin" /> : null}
                Save note
              </Button>
            </div>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function AllowListActionsCell({ entry }: { entry: AllowListEntry }) {
  const confirm = useConfirmationDialog();
  const removeEntry = useMutation(api.admin.removeEmailFromAllowList).withOptimisticUpdate(
    (localStore, args) => {
      const entries = localStore.getQuery(api.admin.listEmailAllowList, {});
      if (!entries) return;

      localStore.setQuery(
        api.admin.listEmailAllowList,
        {},
        entries.filter((item) => item._id !== args.id),
      );
    },
  );
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleDelete = async () => {
    const confirmed = await confirm({
      title: "Remove allow-listed email",
      description: (
        <>
          Remove <strong>{entry.email}</strong> from the sign-in allow-list?
        </>
      ),
      confirmText: "Remove",
      confirmButtonVariant: "destructive",
    });

    if (!confirmed) return;

    setIsDeleting(true);
    try {
      await removeEntry({ id: entry["_id"] });
      toast.success("Email removed from the allow-list.");
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to remove email."));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <EditAllowListEntrySheet entry={entry} />
      <Button
        size="icon-lg"
        variant="ghost"
        onClick={() => void handleDelete()}
        disabled={isDeleting}
        aria-label={`Remove ${entry.email}`}
      >
        {isDeleting ? <Loader2Icon className="animate-spin" /> : <Trash2Icon />}
      </Button>
    </div>
  );
}

function RouteComponent() {
  const entries = useQuery(api.admin.listEmailAllowList);
  const data = React.useMemo(() => entries ?? [], [entries]);

  const columns = React.useMemo<ColumnDef<StockFeatures, AllowListEntry>[]>(
    () => [
      {
        accessorKey: "email",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Email" label="Email" />
        ),
        cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
        meta: { grow: true },
        enableSorting: false,
      },
      {
        accessorKey: "description",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Description" label="Description" />
        ),
        cell: ({ row }) => (
          <span className="truncate text-muted-foreground">
            {row.original.description || "No description"}
          </span>
        ),
        size: 360,
        enableSorting: false,
      },
      {
        id: "createdAt",
        accessorKey: "createdAt",
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created" label="Created" />
        ),
        cell: ({ row }) => <RelativeTimeCard date={new Date(row.original.createdAt)} />,
        size: 180,
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => <AllowListActionsCell entry={row.original} />,
        size: 104,
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [],
  );

  const table = useDataTable({ columns, data });
  const status = entries === undefined ? "LoadingFirstPage" : "Exhausted";

  return (
    <DataTable table={table} status={status} loadMore={() => {}}>
      <DataTableToolbar table={table}>
        <AddAllowListEntrySheet />
      </DataTableToolbar>
    </DataTable>
  );
}
